import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  HStack,
} from "@chakra-ui/react";
import {IoIosRefresh} from "react-icons/io";
import API, { getMediaURL } from "../../context/API";
import SearchBar from "../../theme/Layout/Searchbar";
import TablePagination from "../../commonComponents/Pagination";

import { ResponsiveHeader, ResponsiveTableWrapper } from "../../theme/Layout/ResponsiveWrapper";

function SoldOut({tabIndex, role }) {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // Separate state for refresh loading
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // To store last updated time
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  // Add debounce ref for search
  const searchDebounceRef = useRef(null);


  const totalItems = items.length;

  const { isOpen, onClose } = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const sortByUpdatedAt = (data) => {
    if (!Array.isArray(data)) {
      throw new Error("Input data must be an array.");
    }
    return [...data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  const fetchSoldOutItems = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await API.get(
        `/items/stock?soldOut=true&page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(searchQuery)}`
      );
      const itemsData = sortByUpdatedAt(
        response.data.data.map((item) => ({
          ...item,
          picture: getMediaURL(item.picture),
        }))
      );
      if (response.data.success) {
        setItems(itemsData);
        setFilteredItems(itemsData);
        setLastUpdated(new Date().toLocaleString());
      } else {
        setError("Failed to fetch sold out items.");
      }
    } catch (err) {
      setError("An error occurred while fetching sold out items.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, itemsPerPage, searchQuery]);

  useEffect(() => {
    if (tabIndex === 4) {
      fetchSoldOutItems();
    }
  }, [tabIndex, currentPage, searchQuery, fetchSoldOutItems]);

  const handleToggleSoldOut = async (item) => {
    setTogglingId(item.id);
    try {
      await API.put(`/items/${item.id}`, { sold_out: false });

      toast({
        title: "Item status updated.",
        description: "The item is now available and will be removed from sold out list.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      await fetchSoldOutItems();
      queryClient.invalidateQueries({ queryKey: ["items"] });
      onClose();
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "An error occurred.",
        description: "Unable to update the item.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTogglingId(null);
    }
  };

  const confirmToggleSoldOut = (item) => {
    // onOpen();
    handleToggleSoldOut(item)
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Debounce the search to avoid excessive API calls
    searchDebounceRef.current = setTimeout(() => {
      // Reset to page 1 when searching
      setCurrentPage(1);
      // The search will be handled by the fetchSoldOutItems function
      fetchSoldOutItems();
    }, 300); // 300ms debounce delay
  };

  const RefreshButton = () => (
    <IconButton
      icon={<IoIosRefresh />}
      size={"sm"}
      onClick={fetchSoldOutItems}
      isLoading={refreshing}
      loadingText="Refreshing..."
      mx={4}
      px={2}
    />
  );

  if (loading && !refreshing) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <Box my={"4"}>
      <ResponsiveHeader
        title="Sold out items"
        subtitle="List of all sold out items, you can mark new ones as sold out, they will appear here."
        actions={
          <HStack spacing={2}>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
              Last updated: {lastUpdated || "Never"}
            </Text>
            <RefreshButton />
          </HStack>
        }
      />
      <SearchBar
        items={items.map((item) => item.item)}
        onSearch={handleSearch}
        placeholder="Search items..."
        initialValue={searchQuery}
        variant="filled"
        borderRadius="full"
        width="100%"
      />
      <ResponsiveTableWrapper p={{ base: 2, md: 4 }}>
        <Table variant="simple" borderRadius="md" mt="4" minWidth={{ base: "100%", md: "600px" }}>
          <Thead>
            <Tr>
              <Th fontSize={{ base: "xs", md: "sm" }}>Item</Th>
              <Th fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", md: "table-cell" }}>Description</Th>
              <Th fontSize={{ base: "xs", md: "sm" }}>Price</Th>
              <Th fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", lg: "table-cell" }}>Image</Th>
              <Th fontSize={{ base: "xs", md: "sm" }} textAlign={"center"}>Sold out</Th>
            </Tr>
          </Thead>
          <Tbody>
            {refreshing ? (
              <Tr>
                <Td colSpan="5">
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <Spinner size="lg" />
                  </Flex>
                </Td>
              </Tr>
            ) : filteredItems.length === 0 ? (
              <Tr>
                <Td colSpan="5">
                  <Text textAlign="center">No sold out items found.</Text>
                </Td>
              </Tr>
            ) : (
              filteredItems.map((item) => (
                <Tr key={item.id}>
                  <Td fontSize={{ base: "xs", md: "sm" }}>{item.item}</Td>
                  <Td fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", md: "table-cell" }}>
                    {item.description || "No description available"}
                  </Td>
                  <Td fontSize={{ base: "xs", md: "sm" }}>
                    $
                    {item.price !== null && item.price !== undefined
                      ? (item.price || 0).toFixed(2)
                      : "Free"}
                  </Td>
                  <Td display={{ base: "none", lg: "table-cell" }}>
                    {item.picture && (
                      <Image
                        src={item.picture}
                        alt={item.item}
                        boxSize={{ base: "40px", md: "50px" }}
                        objectFit="cover"
                        borderRadius="md"
                        fallbackSrc="https://via.placeholder.com/50"
                      />
                    )}
                  </Td>
                  <Td>
                    <Flex justifyContent={"center"}>
                      {togglingId === item.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <Switch
                          isChecked={item.sold_out}
                          onChange={() => confirmToggleSoldOut(item)}
                          size={{ base: "sm", md: "md" }}
                          isDisabled={togglingId !== null}
                        />
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <TablePagination
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={itemsPerPage}
          onChange={(page, size) => {
            setCurrentPage(page);
            setItemsPerPage(size);
          }}
          totalLabel="Total sold out items"
          padding="16px 12px"
        />
      </ResponsiveTableWrapper>
      {/* Toggle Sold-Out Status Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Status Change</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to mark this item as available again?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={handleToggleSoldOut}>
              Yes, Make Available
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SoldOut;
